import { withDefault, definedMap } from "collection-utils";

import {
    ComponentKind,
    ComponentDescription,
    InputOutputTables,
    PropertyDescription,
    PropertyKind,
    TableGlideType,
    MutatingScreenKind,
} from "../description";
import { SerializedComponent } from "../../components/SerializedApp";
import { ComponentBuilder, ComponentDescriptor, PropertyDescriptor, ComponentConfiguratorContext } from ".";
import { ComponentHandlerBase } from "./handler";
import { getEnumProperty } from "./property-utils";
import { getDocURL } from "../../docUrl";
import { PropertySection } from "./property-sections";
import { TextBoxStyle, TextBoxJustify, TextBoxLineLimit } from "./text-box-types";
import { SwitchPropertyHandler } from "./property-handlers";
import { IconName } from "../../named-icons";
import { getFeatureSetting } from "../../feature-settings";
import { makeTextPropertyDescriptor } from "./descriptor-utils";
import { makeText } from "../builder-utils";

const ComponentKindTextBox: ComponentKind = "text-box";

const componentName = "Text";

const expand = (val: string, icon?: IconName) => ({
    value: val,
    label: val,
    icon,
});

export interface TextBoxComponentDescription extends ComponentDescription {
    readonly textProperty: PropertyDescription;
    readonly style: PropertyDescription;
    readonly justify: PropertyDescription;
    readonly allCaps: PropertyDescription;
    readonly allowWrapping: PropertyDescription;
    readonly truncate: PropertyDescription;
    readonly lineLimit: PropertyDescription;
}

const defaultStyle = TextBoxStyle.Body;
const textBoxStyleProperty: PropertyDescriptor = {
    kind: PropertyKind.Enum,
    property: { name: "style" },
    label: "Style",
    menuLabel: "Style",
    cases: [
        expand(TextBoxStyle.BodySmall),
        expand(TextBoxStyle.Body),
        expand(TextBoxStyle.BodyLarge),
        expand(TextBoxStyle.Footnote),
        expand(TextBoxStyle.H1),
        expand(TextBoxStyle.H2),
        expand(TextBoxStyle.H3),
        expand(TextBoxStyle.H4),
    ],
    defaultCaseValue: defaultStyle,
    section: PropertySection.Design,
    visual: "dropdown",
};

function getTextBoxStyle(desc: TextBoxComponentDescription): TextBoxStyle {
    return withDefault(getEnumProperty(desc.style), defaultStyle);
}

const defaultJustify = TextBoxJustify.Left;
const textBoxJustifyProperty: PropertyDescriptor = {
    kind: PropertyKind.Enum,
    property: { name: "justify" },
    label: "Align",
    menuLabel: "Align",
    cases: [
        expand(TextBoxJustify.Left, "leftAlign"),
        expand(TextBoxJustify.Center, "centerAlign"),
        expand(TextBoxJustify.Right, "rightAlign"),
        expand(TextBoxJustify.Justify, "justifyAlign"),
    ],
    defaultCaseValue: defaultJustify,
    section: PropertySection.Design,
    visual: "small-images",
};
function getTextBoxJustify(desc: TextBoxComponentDescription): TextBoxJustify {
    return withDefault(getEnumProperty(desc.justify), defaultJustify);
}

const defaultLineLimit = TextBoxLineLimit.Three;
const textBoxLineLimitProperty: PropertyDescriptor = {
    kind: PropertyKind.Enum,
    property: { name: "lineLimit" },
    label: "Lines",
    menuLabel: "Lines",
    cases: [
        expand(TextBoxLineLimit.One),
        expand(TextBoxLineLimit.Two),
        expand(TextBoxLineLimit.Three),
        expand(TextBoxLineLimit.Four),
        expand(TextBoxLineLimit.Five),
        expand(TextBoxLineLimit.Six),
    ],
    defaultCaseValue: defaultLineLimit,
    section: PropertySection.Design,
    visual: "slider",
};
function getTextBoxLineLimit(desc: TextBoxComponentDescription): TextBoxLineLimit {
    return withDefault(getEnumProperty(desc.lineLimit), defaultLineLimit);
}

const allowWrappingPropertyHandler = new SwitchPropertyHandler(
    {
        allowWrapping: true,
    },
    "Allow text wrapping",
    PropertySection.Design
);

const allCapsPropertyHandler = new SwitchPropertyHandler(
    {
        allCaps: false,
    },
    "All Caps",
    PropertySection.Design
);

const truncatePropertyHandler = new SwitchPropertyHandler(
    {
        truncate: false,
    },
    "Truncate text lines",
    PropertySection.Design
);

export class TextBoxComponentHandler extends ComponentHandlerBase<TextBoxComponentDescription> {
    constructor() {
        super(ComponentKindTextBox);
    }

    public get needsColumns(): boolean {
        return false;
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: TextBoxComponentDescription,
        { input }: InputOutputTables
    ): SerializedComponent {
        const { b } = builder;

        const ctx = makeText(builder, desc.textProperty, input);
        if (ctx === undefined) return b.emptyUI;

        const allowWrapping = allowWrappingPropertyHandler.getSwitch(desc);
        const truncate = truncatePropertyHandler.getSwitch(desc);
        let lineLimit = getTextBoxLineLimit(desc);

        if (!truncate || !allowWrapping) {
            lineLimit = TextBoxLineLimit.None;
        }

        return b.ui("TextBox", {
            text: ctx,
            style: b.constant(getTextBoxStyle(desc)),
            justify: b.constant(getTextBoxJustify(desc)),
            lineLimit: b.constant(lineLimit),
            allowWrapping: b.constant(allowWrapping),
            allCaps: b.constant(allCapsPropertyHandler.getSwitch(desc)),
        });
    }

    public getDescriptor(
        desc: TextBoxComponentDescription | undefined,
        _table: TableGlideType | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<TextBoxComponentDescription> {
        const allowWrapping = definedMap(desc, d => allowWrappingPropertyHandler.getSwitch(d)) ?? true;
        const truncate = definedMap(desc, d => truncatePropertyHandler.getSwitch(d)) ?? false;

        const properties: PropertyDescriptor[] = [
            makeTextPropertyDescriptor("textProperty", "Text", "Enter text", true, mutatingScreenKind, {
                isMultiLine: true,
                preferredNames: ["text"],
                columnFirst: true,
            }),
            ...this.getBasePropertyDescriptors(),
            textBoxStyleProperty,
            textBoxJustifyProperty,
            allCapsPropertyHandler,
        ];

        if (allowWrapping) {
            properties.push(truncatePropertyHandler);
            if (truncate) {
                properties.push(textBoxLineLimitProperty);
            }
        }

        return {
            name: componentName,
            helpUrl: getDocURL("text"),
            description: "A box full of text",
            img: "componentBasicText",
            group: "Text",
            isLegacy: !getFeatureSetting("textBox"),
            properties,
        };
    }
}
